import { useNetInfo } from '@react-native-community/netinfo';
import { captureMessage } from '@sentry/react-native';
import { useQueryClient } from '@tanstack/react-query';
import type { TokenResponse } from 'expo-auth-session';
import {
  exchangeCodeAsync,
  refreshAsync,
  revokeAsync,
  useAutoDiscovery
} from 'expo-auth-session';
import { openBrowserAsync } from 'expo-web-browser';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import pkceChallenge from 'react-native-pkce-challenge';

import {
  KEYCLOAK_URL,
  KEYCLOAK_AUTH_REQUEST,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REDIRECT_URL,
  KEYCLOAK_REGISTRATION_URL,
  KEYCLOAK_LOGOUT_URL,
  KEYCLOAK_CHANGE_PASSWORD_URL
} from '@/features/authorization/constants/keycloak';
import {
  TOKEN_REFRESH_RETRY_TIME,
  TOKEN_REFRESH_LEAD_TIME
} from '@/features/authorization/constants/token';
import type { AuthContextValues } from '@/features/authorization/provider/useAuth';
import { AuthContext } from '@/features/authorization/provider/useAuth';
import { useAuthStore } from '@/features/authorization/store/useAuthStore';
import { setIsAuthReady } from '@/features/authorization/utils/authState';
import {
  getTokenExpireTimestamp,
  getIsTokenExpired
} from '@/features/authorization/utils/token';
import { StateView } from '@/shared/components/state-view/StateView';
import { useAppStateEffect } from '@/shared/hooks/useAppStateEffect';
import { sleep } from '@/shared/utils/async';
import { isEmptyOrNull } from '@/shared/utils/string';

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const discovery = useAutoDiscovery(KEYCLOAK_URL);
  /**
   * Note: isConnected can be null, thus isConnected === false guarantees that the connectivity is checked properly
   */
  const { isConnected } = useNetInfo();
  const queryClient = useQueryClient();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [serverTimeOffset, setServerTimeOffset] = useState<number>(0);
  const token = useAuthStore((state) => state.token);
  const updateToken = useAuthStore((state) => state.updateToken);
  const resetStore = useAuthStore((state) => state.reset);

  /**
   * Update context states and store values when token is updated
   * @param tokenResponse Fetched token response from login or refresh calls
   */
  const updateStates = useCallback(
    (tokenResponse?: TokenResponse) => {
      updateToken(tokenResponse);
      setIsAuthenticated(!!tokenResponse);
    },
    [updateToken]
  );

  /**
   * Clear all states
   */
  const clearState = useCallback(() => {
    // Clear auth states
    resetStore();
    setIsAuthenticated(false);
    // Clear query states
    queryClient.resetQueries();
    queryClient.removeQueries();
  }, [queryClient, resetStore]);

  /**
   * Trigger login inside an in-app browser
   */
  const login = useCallback(async () => {
    try {
      const authorizeResult = await KEYCLOAK_AUTH_REQUEST.promptAsync(
        discovery!
      );

      // If successful, get token
      if (authorizeResult.type === 'success') {
        const code_verifier = KEYCLOAK_AUTH_REQUEST.codeVerifier ?? '';
        const tokenResponse = await exchangeCodeAsync(
          {
            code: authorizeResult.params.code,
            clientId: KEYCLOAK_CLIENT_ID,
            redirectUri: KEYCLOAK_REDIRECT_URL,
            extraParams: { code_verifier }
          },
          discovery!
        );

        updateStates(tokenResponse);
      }
    } catch (error) {
      console.error(
        '[AUTH_PROVIDER]: Something went wrong during login:',
        error
      );
      clearState();
    }
  }, [clearState, discovery, updateStates]);

  const signup = useCallback(async () => {
    try {
      const { codeChallenge, codeVerifier } = pkceChallenge();

      const authorizeResult = await KEYCLOAK_AUTH_REQUEST.promptAsync(
        discovery!,
        {
          url: `${KEYCLOAK_REGISTRATION_URL}&state=${KEYCLOAK_AUTH_REQUEST.state}&code_challenge=${codeChallenge}&code_challenge_method=S256`
        }
      );

      // If successful, get token
      if (authorizeResult.type === 'success') {
        const tokenResponse = await exchangeCodeAsync(
          {
            code: authorizeResult.params.code,
            clientId: KEYCLOAK_CLIENT_ID,
            redirectUri: KEYCLOAK_REDIRECT_URL,
            extraParams: { code_verifier: codeVerifier }
          },
          discovery!
        );

        updateStates(tokenResponse);
      }
    } catch (error) {
      console.error(
        '[AUTH_PROVIDER]: Something went wrong during signup:',
        error
      );
      clearState();
    }
  }, [clearState, discovery, updateStates]);

  /**
   * Trigger logout
   * Note: even if logout request fails, context states and store values are cleared and all queries are cleared
   * Note: revokeAsync revokes the token but it is not a log out since the browser still keeps the user logged-in
   * On next login the user still has to enter the login credentials but the token of already logged-in account is forwarded to the app
   * The direct fetch to keycloak guaranties that the user is logged out
   */
  const logout = useCallback(async () => {
    const accessToken = token?.accessToken;
    if (isEmptyOrNull(accessToken)) return;

    try {
      fetch(`${KEYCLOAK_LOGOUT_URL}${token?.idToken}`);
      clearState();
      if (!discovery) return;

      await revokeAsync(
        { token: accessToken, clientId: KEYCLOAK_CLIENT_ID },
        discovery
      );
    } catch (error) {
      console.error(
        '[AUTH_PROVIDER]: Something went wrong during logout:',
        error
      );
    }
  }, [clearState, discovery, token]);

  /**
   * Refresh token
   * Note: in case of no refresh token or request fail all states are cleared
   */
  const refreshAccessToken = useCallback(
    async (tokenExpireTime?: number) => {
      const refreshToken = token?.refreshToken;

      if (isEmptyOrNull(refreshToken)) {
        console.warn(
          '[AUTH_PROVIDER]: No refresh token available. Skipping token refresh.'
        );
        clearState();
        return;
      }

      if (!discovery) {
        console.warn(
          '[AUTH_PROVIDER]: Discovery has not yet been performed. Skipping token refresh.'
        );
        return;
      }

      let shouldRetry;
      do {
        try {
          shouldRetry = false;

          const tokenResponse = await refreshAsync(
            {
              clientId: KEYCLOAK_CLIENT_ID,
              refreshToken
            },
            discovery
          );

          const currentServerTime = tokenResponse.issuedAt * 1000;
          const serverTimeOffset = currentServerTime - Date.now();
          setServerTimeOffset(serverTimeOffset);

          updateStates(tokenResponse);
        } catch (error) {
          if (tokenExpireTime) {
            // We may only be offline momentarily (e.g., due to a network change or similar).
            // As long as we still have time, we will continue to try.
            const nextRetryTime = Date.now() + TOKEN_REFRESH_RETRY_TIME;
            if (nextRetryTime < tokenExpireTime) {
              console.warn(
                `[AUTH_PROVIDER]: Token could not be refreshed. Retrying after ${TOKEN_REFRESH_RETRY_TIME / 1000} sec`,
                error
              );
              await sleep(TOKEN_REFRESH_RETRY_TIME);
              shouldRetry = true;
            } else {
              console.error(
                '[AUTH_PROVIDER]: Token could not be refreshed. No further retries are possible',
                error
              );
              captureMessage(
                '[AUTH_PROVIDER]: Token could not be refreshed. No further retries are possible',
                (scope) => {
                  scope.setLevel('error');
                  scope.setTag(
                    'customEventName',
                    'TOKEN_REFRESH_RETRIES_EXCEEDED'
                  );
                  return scope;
                }
              );
            }
          } else {
            console.error(
              '[AUTH_PROVIDER]: Token could not be refreshed',
              error
            );
            captureMessage(
              '[AUTH_PROVIDER]: Token could not be refreshed.',
              (scope) => {
                scope.setLevel('error');
                scope.setTag('customEventName', 'TOKEN_REFRESH_FAILED');
                return scope;
              }
            );
          }
        }
      } while (shouldRetry);
    },
    [updateStates, clearState, discovery, token?.refreshToken]
  );

  /**
   * Continuously refresh the token if it is about to expire.
   * Note: runs only if token is not undefined and user is authenticated
   */
  useEffect(() => {
    const scheduleNextTokenRefresh = (token: TokenResponse) => {
      const localTokenExpireTime =
        getTokenExpireTimestamp(token) - serverTimeOffset;
      const refreshDelay =
        localTokenExpireTime - Date.now() - TOKEN_REFRESH_LEAD_TIME;

      // Only the next token refresh is queued as a one-time call.
      // During the refresh, the token changes and the effect is triggered again for the next refresh to be queued.
      return setInterval(async () => {
        await refreshAccessToken(localTokenExpireTime);
      }, refreshDelay);
    };

    if (token && isAuthenticated) {
      const refreshTimer = scheduleNextTokenRefresh(token);
      return () => clearInterval(refreshTimer);
    }
    // No token / not authenticated yet: nothing to schedule. This is the
    // normal state on app start until the token check completes — no warning.
  }, [
    isAuthenticated,
    refreshAccessToken,
    token,
    token?.expiresIn,
    serverTimeOffset
  ]);

  const changePassword = useCallback(async () => {
    await openBrowserAsync(KEYCLOAK_CHANGE_PASSWORD_URL);
  }, []);

  /**
   * Boolean which determines if the provider is ready
   * discovery is ready or user is offline (allow user into the app in user is offline but maybe not authenticated otherwise user might be stuck here in a loading view)
   */
  const [isReady, setIsReady] = useState<boolean>(false);

  const refreshTokenIfNecessary = useCallback(
    async (cancelled?: boolean) => {
      // Allow offline users in if token not expired (avoid being stuck on loading)
      if (isConnected === false) {
        // TODO split isAuthenticated (used to determine if requests should be made) into two attributes (one valid token, and one existing token one)
        setIsAuthenticated(!isEmptyOrNull(token?.accessToken));
        if (!cancelled) {
          setIsAuthReady();
          setIsReady(true);
        }
        return;
      }

      // Wait for discovery before any auth action
      if (!discovery) return;

      /**
       * Check on app start if the stored token is expired
       * If not set is authenticated to true,
       * Otherwise try to refresh the token with stored refreshToken
       */
      if (getIsTokenExpired()) {
        await refreshAccessToken(); // await refresh to finish
      } else {
        setIsAuthenticated(true);
      }

      if (!cancelled) {
        setIsAuthReady();
        setIsReady(true);
      }
    },
    [discovery, isConnected, refreshAccessToken, token?.accessToken]
  );

  /**
   * Check the stored token once the discovery document is available (the
   * check is a no-op before that). Re-runs when connectivity changes, e.g.
   * to let an offline user in once the netinfo state is resolved.
   */
  useEffect(() => {
    let cancelled = false;
    refreshTokenIfNecessary(cancelled);
    return () => {
      cancelled = true;
    };
  }, [discovery, isConnected, refreshTokenIfNecessary]);

  useAppStateEffect({
    onForeground: refreshTokenIfNecessary
  });

  const contextValues: AuthContextValues = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout,
      signup,
      changePassword,
      refreshTokenIfNecessary
    }),
    [
      changePassword,
      isAuthenticated,
      login,
      logout,
      refreshTokenIfNecessary,
      signup
    ]
  );

  return (
    <AuthContext.Provider value={contextValues}>
      <StateView asView isLoading={!isReady}>
        {children}
      </StateView>
    </AuthContext.Provider>
  );
};
